import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Divider,
    Flex,
    Grid,
    GridItem,
    Stack,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import { formatDocument } from "utils/utils";
import { userService } from "services/userService";
import UserTimeline from "./UserTimeline";
import { ROUTES } from "constants/routes-path";

function UserDetails() {
    const { id } = useParams();
    const { t } = useTranslation();
    const textColor = useColorModeValue("gray.700", "white");
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const navigate = useNavigate();

    const [userData, setUserData] = useState([]);
    const [historyData, setHistoryData] = useState([]);

    useEffect(() => {
        if (id) {
            fetchUser(id);
        }
    }, [id]);

    const fetchUser = async (id) => {
        try {
            const user = await userService.find(id);
            setUserData(user);

            const userHistory = user.historico ? JSON.parse(user.historico) : [];
            const personHistory = user.pessoa.historico ? JSON.parse(user.pessoa.historico) : [];

            setHistoryData(
                [...userHistory, ...personHistory].sort((a, b) => new Date(a.data) - new Date(b.data))
            );
        } catch (error) {
            console.error(t('settings.user.messages.errors.list'), error);
        }
    };

    return (
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
            <Card px="0px" mb="24px">
                <CardHeader px="22px" mb="24px">
                    <Flex direction="column">
                        <Text color={textColor} fontSize="lg" fontWeight="bold">
                            {t("system.actions.details")}
                        </Text>
                    </Flex>
                </CardHeader>
                <CardBody px="22px">
                    <Tabs variant="enclosed">
                        <TabList>
                            <Tab>{t("settings.user.details.tab.general.title")}</Tab>
                            <Tab>{t("settings.user.details.tab.person.title")}</Tab>
                            {userData.matricula && (
                                <Tab>{t("settings.user.details.tab.agent.title")}</Tab>
                            )}
                            <Tab>{t("settings.user.details.tab.timeline.title")}</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel>
                                <Card border="1px solid #3884cc" bg={cardBg}>
                                    <CardHeader>
                                        <Text color={textColor} fontSize="md" fontWeight="bold">
                                            {t("settings.user.details.tab.general.subtitle")}
                                        </Text>
                                    </CardHeader>
                                    <Divider borderColor="#3884cc" mb="20px" />
                                    <CardBody>
                                        <Stack spacing={4}>
                                            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.user.details.tab.general.login")}:
                                                    </Text>{"\u00A0"}
                                                    {userData.login ?? "-"}
                                                </GridItem>
                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.user.details.tab.general.function")}:
                                                    </Text>{"\u00A0"}
                                                    {userData.funcao || "-"}
                                                </GridItem>
                                                <GridItem fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("settings.user.details.tab.general.active")}:
                                                    </Text>{"\u00A0"}
                                                    {userData.ativo ? t("system.labels.true") : t("system.labels.false")}
                                                </GridItem>
                                            </Grid>
                                        </Stack>
                                    </CardBody>
                                </Card>
                            </TabPanel>

                            <TabPanel>
                                {userData.pessoa ? (
                                    <Card border="1px solid #3884cc" bg={cardBg}>
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("settings.user.details.tab.person.title")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={4}>
                                                <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.cpfcnpj")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.cpf_cnpj ? formatDocument(userData.pessoa.cpf_cnpj) : "-"}
                                                        <Text as="span" fontWeight="bold" ml={4}>
                                                            {t("settings.user.details.tab.person.surname")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.apelido ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.name")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.nome ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.social-name")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.nome_social ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.rg")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.numero_identidade ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.uf-rg")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.id_uf_identidade ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.issuing-agency")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.orgao_expedidor_identidade ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.phone")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.telefone ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.cellphone")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.celular ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.email")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.email ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.address-street")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.endereco_logradouro ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.address-number")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.endereco_numero ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.address-district")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.endereco_bairro ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.address-city")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.endereco_municipio ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.address-state")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.endereco_uf ?? "-"}
                                                    </GridItem>

                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.person.address-zip-code")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.pessoa.endereco_cep ?? "-"}
                                                    </GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                ) : (
                                    <Text color={textColor} fontSize="md">-</Text>
                                )}
                            </TabPanel>

                            {userData.matricula && (
                                <TabPanel>
                                    <Card border="1px solid #3884cc" bg={cardBg}>
                                        <CardHeader>
                                            <Text color={textColor} fontSize="md" fontWeight="bold">
                                                {t("settings.user.details.tab.agent.title")}
                                            </Text>
                                        </CardHeader>
                                        <Divider borderColor="#3884cc" mb="20px" />
                                        <CardBody>
                                            <Stack spacing={4}>
                                                <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6}>
                                                    <GridItem fontSize="md">
                                                        <Text as="span" fontWeight="bold">
                                                            {t("settings.user.details.tab.agent.registration")}:
                                                        </Text>{"\u00A0"}
                                                        {userData.matricula ?? "-"}
                                                    </GridItem>
                                                </Grid>
                                            </Stack>
                                        </CardBody>
                                    </Card>
                                </TabPanel>
                            )}

                            <TabPanel>
                                <Card border="1px solid #3884cc" mb="30px" bg={cardBg}>
                                    <UserTimeline
                                        creationDate={userData?.data_criacao}
                                        historyData={historyData}
                                    />
                                </Card>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                    <Flex justifyContent="flex-start">
                        <Button
                            variant="light"
                            fontSize="sm"
                            alignSelf="flex-end"
                            mt="24px"
                            w="125px"
                            onClick={() => navigate(ROUTES.usersPath)}
                        >
                            {t("system.actions.back")}
                        </Button>
                    </Flex>
                </CardBody>
            </Card>
        </Flex >
    );
}

export default UserDetails;